import { Assert } from "../../Assert";
import { PartialNullable } from "../../Extensions";
import { StringHelper } from "../../Helpers/StringHelper";
import { IJNativeEvent } from "../../JNative";
import { PropertyChange } from "../../PropertyChange";
import { Property } from "../../RadObject";
import { IInputControl, InputControl, InputControlPropertyValues } from "./InputControl";
import { TextBox, TextBoxTypeEnum } from "./TextBox";
import { NAMESPACE } from "./_Namespace";

import "./TimeSpan.scss";

export type TimeSpanPropertyValues =
    InputControlPropertyValues
    & {
    };

export type TimeSpanProperties = {
    [name in keyof TimeSpanPropertyValues]: Property<TimeSpanPropertyValues[name]>
};

export interface ITimeSpan extends IInputControl, TimeSpanProperties
{
}

export class TimeSpan extends InputControl implements ITimeSpan
{
    public static override classNamespace = NAMESPACE + "TimeSpan";
    public static override className = "TimeSpan";

    // #region Global Variables

    protected days?: TextBox;
    protected hours?: TextBox;
    protected minutes?: TextBox;
    protected seconds?: TextBox;

    private _setValueSuper = false;

    // #endregion

    // #region Constructors

    constructor(propertyValues?: PartialNullable<TimeSpanPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-time-span");
    }

    // #endregion

    // #region Protected Methods

    protected _elementValueChanged()
    {
        if (!this.__processElementValueChanged)
            return;

        this._setValueSuper = true;
        this.value.set(this._elementValueGet());
        this._setValueSuper = false;
    }
    protected _elementValueGet()
    {
        Assert.isNotNullOrUndefined(this.days, "this.days");
        Assert.isNotNullOrUndefined(this.hours, "this.hours");
        Assert.isNotNullOrUndefined(this.minutes, "this.minutes");
        Assert.isNotNullOrUndefined(this.seconds, "this.seconds");

        const
            days = parseInt(this.days.value.get()),
            hours = parseInt(this.hours.value.get()),
            minutes = parseInt(this.minutes.value.get()),
            seconds = parseInt(this.seconds.value.get());

        let
            value = null;

        if (!isNaN(days) && !isNaN(hours) && !isNaN(minutes) && !isNaN(seconds))
            value = StringHelper.format("{0}.{1}:{2}:{3}", days, StringHelper.padLeft(hours.toString(), 2, "0"), StringHelper.padLeft(minutes.toString(), 2, "0"), StringHelper.padLeft(seconds.toString(), 2, "0"));

        return value;
    }
    protected _elementValueSet(value: any)
    {
        Assert.isNotNullOrUndefined(this.days, "this.days");
        Assert.isNotNullOrUndefined(this.hours, "this.hours");
        Assert.isNotNullOrUndefined(this.minutes, "this.minutes");
        Assert.isNotNullOrUndefined(this.seconds, "this.seconds");

        this.__processElementValueChanged = false;

        if (!value)
        {
            this.days.value.set(null);
            this.hours.value.set(null);
            this.minutes.value.set(null);
            this.seconds.value.set(null);
        }
        else
        {
            this.days.value.set(parseInt(value.substr(0, value.length - 9)));
            this.hours.value.set(parseInt(value.substr(value.length - 8, 2)));
            this.minutes.value.set(parseInt(value.substr(value.length - 5, 2)));
            this.seconds.value.set(parseInt(value.substr(value.length - 2, 2)));
        }

        this.__processElementValueChanged = true;
    }

    // #endregion

    // #region Overrides

    protected override _bindEvents()
    {
        super._bindEvents();

        Assert.isNotNullOrUndefined(this.days, "this.days");
        Assert.isNotNullOrUndefined(this.hours, "this.hours");
        Assert.isNotNullOrUndefined(this.minutes, "this.minutes");
        Assert.isNotNullOrUndefined(this.seconds, "this.seconds");

        this.days.on("valueChanged", { self: this }, this.onDaysValueChanged);
        this.hours.on("valueChanged", { self: this }, this.onHoursValueChanged);
        this.minutes.on("valueChanged", { self: this }, this.onMinutesValueChanged);
        this.seconds.on("valueChanged", { self: this }, this.onSecondsValueChanged);
    }
    protected override _createElement()
    {
        const
            $$element = super._createElement();

        this.days = new TextBox({ inline: true, placeholder: "Days", type: TextBoxTypeEnum.number }).appendTo($$element);
        this.hours = new TextBox({ inline: true, maximumLength: 2, placeholder: "Hours", type: TextBoxTypeEnum.number }).appendTo($$element);
        this.minutes = new TextBox({ inline: true, maximumLength: 2, placeholder: "Mins", type: TextBoxTypeEnum.number }).appendTo($$element);
        this.seconds = new TextBox({ inline: true, maximumLength: 2, placeholder: "Secs", type: TextBoxTypeEnum.number }).appendTo($$element);

        this.labelTargetId = this.days.labelTargetId;

        this.value.overrideSet((value, next) =>
        {
            if (this._setValueSuper)
                next(value);
            else
            {
                this._elementValueSet(value);
                next(this._elementValueGet());
            }
        });

        return $$element;
    }
    protected override _enabledChanged(change: PropertyChange<boolean>)
    {
        super._enabledChanged(change);

        Assert.isNotNullOrUndefined(this.days, "this.days");
        Assert.isNotNullOrUndefined(this.hours, "this.hours");
        Assert.isNotNullOrUndefined(this.minutes, "this.minutes");
        Assert.isNotNullOrUndefined(this.seconds, "this.seconds");

        this.days.enabled.set(change.currentValue);
        this.hours.enabled.set(change.currentValue);
        this.minutes.enabled.set(change.currentValue);
        this.seconds.enabled.set(change.currentValue);
    }
    public override remove()
    {
        if (this.days)
        {
            this.days.remove();
            delete this.days;
        }

        if (this.hours)
        {
            this.hours.remove();
            delete this.hours;
        }

        if (this.minutes)
        {
            this.minutes.remove();
            delete this.minutes;
        }

        if (this.seconds)
        {
            this.seconds.remove();
            delete this.seconds;
        }

        super.remove();
    }

    // #endregion

    // #region Event Handlers

    protected onDaysValueChanged(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        self._elementValueChanged();
    }
    protected onHoursValueChanged(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        self._elementValueChanged();
    }
    protected onMinutesValueChanged(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        self._elementValueChanged();
    }

    protected onSecondsValueChanged(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        self._elementValueChanged();
    }

    // #endregion
}

